import {
    Breadcrumb,
    Button,
    Col,
    Form,
    Input,
    Row,
    Select,
    Spin,
    message,
} from "antd";
import AutoOpenSelect from "../SelectComponent/AutoOpenSelect";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import "react-phone-input-2/lib/style.css";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ReactComponent as Back } from "../../Images/Back.svg";
import {
    fetchCustomerDropdown,
    fetchCustomerList,
    resetCurrentCustomerPage,
    resetCustomerList,
} from "../../slices/customer";
import {
    fetchDieProfileDropdown,
    resetDieProfileData,
    resetDieProfileList,
} from "../../slices/die-profile";
import {
    fetchAlloysList,
    resetCurrentAlloyListPage,
    resetAlloyList,
    fetchAlloyDropdown,
} from "../../slices/alloy";
import {
    fetchTemperDropdown,
    fetchTempersList,
    resetCurrentTemperPage,
    resetTempersList,
} from "../../slices/tempers";
import { fetchConversionRateById, updateConversion } from "../../slices/conversion-rate";
import { createNumberValidationRule } from "../../utils/CreateNumberValidationRule";
import { debounce } from "lodash";

const { Option } = Select;

function EditConversionRate() {
    const [form] = Form.useForm();
    let navigate = useNavigate();
    const { id } = useParams();
    const [btnLoading, setBtnLoading] = useState(false);
    const [searchCustomer, setSearchCustomer] = useState("");
    const [searchDieProfile, setSearchDieProfile] = useState("");
    const [searchAlloy, setSearchAlloy] = useState("");
    const [searchTemper, setSearchTemper] = useState("");

    const dispatch = useDispatch();
    const {
        list: customerList,
        loading: customerLoading,
        currentPage: customerCurrentPage,
        hasMore: customerHasMore,
    } = useSelector((state) => state.customer);
    const {
        list: dieProfileList,
        loading: dieProfileLoading,
        currentPage: dieProfileCurrentPage,
        hasMore: dieProfileHasMore,
    } = useSelector((state) => state.dieProfile);
    const {
        list: alloyList,
        loading: alloyLoading,
        currentPage: alloyCurrentPage,
        hasMore: alloyHasMore,
    } = useSelector((state) => state.alloy);
    const {
        list: temperList,
        loading: temperLoading,
        currentPage: temperCurrentPage,
        hasMore: temperHasMore,
    } = useSelector((state) => state.temper);

    useEffect(() => {
        dispatch(resetCustomerList());
        dispatch(resetDieProfileList());
        dispatch(resetAlloyList());
        dispatch(resetTempersList());
        dispatch(fetchCustomerDropdown({ page: 1, pagesize: 50000 }));
        dispatch(fetchDieProfileDropdown({ page: 1, pagesize: 50000 }));
        dispatch(fetchAlloyDropdown({ page: 1, pagesize: 50000 }));
        dispatch(fetchTemperDropdown({ page: 1, pagesize: 50000 }));
    }, [dispatch]);

    useEffect(() => {
        const fetchConversionRate = async () => {
            try {
                const response = await dispatch(fetchConversionRateById(id)).unwrap();
                if (response && response.results && response.results.data) {
                    const conversionData = response.results.data;
                    form.setFieldsValue({
                        customer: conversionData[0]?.customer_id,
                        conversion_rate_items: conversionData.map((item) => ({
                            id: item.id,
                            die_profile: item.die_id,
                            alloy: item.alloy_id,
                            temper: item.temper_id,
                            conversion: item.conversion,
                            remarks: item.remarks || "",
                        })),
                    });
                    setSearchCustomer(conversionData[0]?.customer_id);
                } else {
                    message.error("Invalid conversion rate data received.");
                }
            } catch (error) {
                message.error("Failed to fetch conversion rate data.");
            }
        };
        fetchConversionRate();
    }, [id, form, dispatch]);

    const handleSearchCustomer = (value) => {
        setSearchCustomer(value);
        if (value) {
            dispatch(resetCustomerList());
            dispatch(fetchCustomerList({ page: 1, pagesize: 100, search: value }));
        } else {
            setSearchCustomer("");
            dispatch(resetCustomerList());
            dispatch(resetCurrentCustomerPage());
            dispatch(fetchCustomerList({ page: 1, pagesize: 10 }));
        }
    };

    const handleChangeCustomer = (value) => {
        setSearchCustomer(value);
    };

    const debouncedSearchDieProfile = debounce((value, dispatch) => {
        dispatch(resetDieProfileData());
        dispatch(
            fetchDieProfileDropdown({
                page: 1,
                pagesize: 50,
                search: value || "",
            })
        );
    }, 300);

    const handleSearchDieProfile = (value) => {
        setSearchDieProfile(value);
        if (value) {
            debouncedSearchDieProfile(value, dispatch);
        } else {
            dispatch(resetDieProfileData());
            dispatch(fetchDieProfileDropdown({ page: 1, pagesize: 50 }));
        }
    };

    const onScrollDieProfile = (event) => {
        const target = event.target;
        if (
            !dieProfileLoading &&
            dieProfileHasMore &&
            target.scrollHeight - target.scrollTop - target.clientHeight < 50
        ) {
            const nextPage = dieProfileCurrentPage + 1;
            dispatch(
                fetchDieProfileDropdown({
                    page: nextPage,
                    pagesize: 50,
                    search: searchDieProfile || "",
                })
            );
        }
    };

    const handleChangeDieProfile = (value) => {
        setSearchDieProfile(value);
    };

    const handleSearchAlloy = (value) => {
        setSearchAlloy(value);
        if (value) {
            dispatch(resetAlloyList());
            dispatch(fetchAlloysList({ page: 1, pagesize: 100, search: value }));
        } else {
            setSearchAlloy("");
            dispatch(resetAlloyList());
            dispatch(resetCurrentAlloyListPage());
            dispatch(fetchAlloysList({ page: 1, pagesize: 10 }));
        }
    };

    const handleChangeAlloy = (value) => {
        setSearchAlloy(value);
    };

    const handleSearchTemper = (value) => {
        setSearchTemper(value);
        if (value) {
            dispatch(resetTempersList());
            dispatch(fetchTempersList({ page: 1, pagesize: 100, search: value }));
        } else {
            setSearchTemper("");
            dispatch(resetTempersList());
            dispatch(resetCurrentTemperPage());
            dispatch(fetchTempersList({ page: 1, pagesize: 10 }));
        }
    };

    const handleChangeTemper = (value) => {
        setSearchTemper(value);
    };

    const onScrollCustomer = (event) => {
        const target = event.target;
        if (
            !customerLoading &&
            customerHasMore &&
            target.scrollTop + target.offsetHeight === target.scrollHeight
        ) {
            dispatch(fetchCustomerList({ page: customerCurrentPage, pagesize: 10 }));
        }
    };

    const onScrollAlloy = (event) => {
        const target = event.target;
        if (
            !alloyLoading &&
            alloyHasMore &&
            target.scrollTop + target.offsetHeight === target.scrollHeight
        ) {
            dispatch(fetchAlloysList({ page: alloyCurrentPage, pagesize: 10 }));
        }
    };

    const onScrollTemper = (event) => {
        const target = event.target;
        if (
            !temperLoading &&
            temperHasMore &&
            target.scrollTop + target.offsetHeight === target.scrollHeight
        ) {
            dispatch(fetchTempersList({ page: temperCurrentPage, pagesize: 10 }));
        }
    };

    const handleFinish = (values) => {
        setBtnLoading(true);
        const transformedPayload = {
            conversion_rates: values.conversion_rate_items.map((item) => {
                const payloadItem = {
                    customer: values.customer,
                    die: item.die_profile,
                    alloy: item.alloy,
                    temper: item.temper,
                    conversion: parseFloat(item.conversion),
                    remarks: item.remarks || "",
                };
                if (item.id) {
                    payloadItem.id = item.id;
                }
                return payloadItem;
            }),
        };

        dispatch(updateConversion({ values: transformedPayload }))
            .unwrap()
            .then((result) => {
                setBtnLoading(false);
                if (result.success) {
                    message.success("Conversion Rate Item Updated Successfully!");
                    navigate("/conversion-rate-list");
                }
            })
            .catch((error) => {
                setBtnLoading(false);
                if (typeof error === "object" && error !== null) {
                    const messages = [];
                    for (const key in error) {
                        if (Array.isArray(error[key])) {
                            messages.push(`${key}: ${error[key].join(", ")}`);
                        }
                    }
                    if (messages.length > 0) {
                        message.error(messages.join("; "));
                    } else {
                        message.error("An unknown error occurred.");
                    }
                } else if (typeof error === "string") {
                    message.error(error);
                } else if (error instanceof Error) {
                    message.error(error.message);
                } else {
                    message.error("An unknown error occurred.");
                }
            });
    };

    const onFinishFailed = (errorInfo) => { };

    return (
        <>
            <div className="sticyaction">
                <div className="pagenamerow mb-0">
                    <h2>Edit Conversion Rate Item</h2>
                    <div className="bredcrumbwrp">
                        <Link onClick={() => navigate(-1)} className="back">
                            <Back /> BACK
                        </Link>
                        <Breadcrumb
                            items={[
                                { title: <Link to="/dashboard">Home</Link> },
                                {
                                    title: <Link to="/conversion-rate-list">Conversion Rates</Link>,
                                },
                                { title: "Edit Conversion Rate Item" },
                            ]}
                        />
                    </div>
                </div>
            </div>

            <div className="formwrapper">
                <Form
                    layout="vertical"
                    form={form}
                    onFinish={handleFinish}
                    onFinishFailed={onFinishFailed}
                    autoComplete="off"
                    initialValues={{
                        conversion_rate_items: [{}],
                    }}
                >
                    <Row gutter={24}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label="Customer"
                                name="customer"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please Select Customer",
                                    },
                                ]}
                            >
                                <AutoOpenSelect
                                    placeholder="Select Customer"
                                    style={{ width: "100%" }}
                                    loading={customerLoading}
                                    onPopupScroll={onScrollCustomer}
                                    value={searchCustomer}
                                    onChange={handleChangeCustomer}
                                    onSearch={handleSearchCustomer}
                                    allowClear
                                    showSearch={true}
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                    notFoundContent={customerLoading ? "Loading..." : "No options"}
                                    dropdownRender={(menu) => <>{menu}</>}
                                >
                                    {[
                                        ...new Map(customerList.map((item) => [item.id, item])).values(),
                                    ].map((el) => (
                                        <Option key={el.id} value={el.id} disabled={el.deleted === 1}>
                                            {el.customer_name}
                                        </Option>
                                    ))}
                                    {customerLoading && <Option>Loading...</Option>}
                                </AutoOpenSelect>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.List name="conversion_rate_items">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }, index) => (
                                    <Row
                                        className="rowItem mx-0"
                                        gutter={[24, 24]}
                                        align="middle"
                                        key={key}
                                        style={{
                                            width: "100%",
                                            border: "1px solid #d9d9d9",
                                            borderRadius: "6px",
                                            padding: "16px",
                                            marginBottom: "16px",
                                            position: "relative",
                                        }}
                                    >
                                        <Col flex="auto">
                                            <Row gutter={24}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, "id"]}
                                                    noStyle
                                                >
                                                    <Input type="hidden" />
                                                </Form.Item>

                                                <Col sm={3}>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, "die_profile"]}
                                                        label="Profile Number"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: "Please select a profile number",
                                                            },
                                                        ]}
                                                    >
                                                        <AutoOpenSelect
                                                            placeholder="Select Profile No"
                                                            style={{ width: "100%" }}
                                                            loading={dieProfileLoading}
                                                            onPopupScroll={onScrollDieProfile}
                                                            value={searchDieProfile}
                                                            onChange={handleChangeDieProfile}
                                                            onSearch={handleSearchDieProfile}
                                                            allowClear
                                                            showSearch={true}
                                                            filterOption={(input, option) =>
                                                                option.children.toLowerCase().includes(input.toLowerCase())
                                                            }
                                                            onClear={() => {
                                                                dispatch(resetDieProfileData());
                                                                dispatch(fetchDieProfileDropdown({ page: 1, pagesize: 50 }));
                                                            }}
                                                            notFoundContent={
                                                                dieProfileLoading ? <Spin size="small" /> : "No options"
                                                            }
                                                            dropdownRender={(menu) => <>{menu}</>}
                                                        >
                                                            {[
                                                                ...new Map(dieProfileList.map((item) => [item.id, item])).values(),
                                                            ].map((el) => (
                                                                <Option key={el.id} value={el.id} disabled={el.deleted === 1}>
                                                                    {el.die_number}
                                                                </Option>
                                                            ))}
                                                            {dieProfileLoading && <Option>Loading...</Option>}
                                                        </AutoOpenSelect>
                                                    </Form.Item>
                                                </Col>

                                                <Col sm={3}>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, "alloy"]}
                                                        label="Alloy"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: "Please select alloy",
                                                            },
                                                        ]}
                                                    >
                                                        <AutoOpenSelect
                                                            placeholder="Select Alloy"
                                                            style={{ width: "100%" }}
                                                            loading={alloyLoading}
                                                            onPopupScroll={onScrollAlloy}
                                                            value={searchAlloy}
                                                            onChange={handleChangeAlloy}
                                                            onSearch={handleSearchAlloy}
                                                            allowClear
                                                            showSearch={true}
                                                            filterOption={(input, option) => {
                                                                if (!input) return true;
                                                                const optionText = (option?.children || "")
                                                                    .toString()
                                                                    .replace(/\s+/g, "")
                                                                    .toLowerCase();
                                                                const inputText = input.replace(/\s+/g, "").toLowerCase();
                                                                return optionText.includes(inputText);
                                                            }}
                                                            notFoundContent={alloyLoading ? "Loading..." : "No options"}
                                                            dropdownRender={(menu) => <>{menu}</>}
                                                        >
                                                            {[
                                                                ...new Map(alloyList.map((item) => [item.id, item])).values(),
                                                            ].map((el) => (
                                                                <Option key={el.id} value={el.id} disabled={el.deleted === 1}>
                                                                    {el.name} - {el.color_code}
                                                                </Option>
                                                            ))}
                                                            {alloyLoading && <Option>Loading...</Option>}
                                                        </AutoOpenSelect>
                                                    </Form.Item>
                                                </Col>

                                                <Col sm={3}>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, "temper"]}
                                                        label="Temper"
                                                        rules={[{ required: true, message: "Please select temper" }]}
                                                    >
                                                        <AutoOpenSelect
                                                            placeholder="Select Temper"
                                                            style={{ width: "100%" }}
                                                            loading={temperLoading}
                                                            onPopupScroll={onScrollTemper}
                                                            value={searchTemper}
                                                            onChange={handleChangeTemper}
                                                            onSearch={handleSearchTemper}
                                                            allowClear
                                                            showSearch={true}
                                                            filterOption={(input, option) => {
                                                                if (!input) return true;
                                                                const optionText = (option?.children || "")
                                                                    .toString()
                                                                    .replace(/\s+/g, "")
                                                                    .toLowerCase();
                                                                const inputText = input.replace(/\s+/g, "").toLowerCase();
                                                                return optionText.includes(inputText);
                                                            }}
                                                            notFoundContent={temperLoading ? "Loading..." : "No options"}
                                                            dropdownRender={(menu) => <>{menu}</>}
                                                        >
                                                            {[
                                                                ...new Map(temperList.map((item) => [item.id, item])).values(),
                                                            ].map((el) => (
                                                                <Option key={el.id} value={el.id} disabled={el.deleted === 1}>
                                                                    {el.name} - {el.code}
                                                                </Option>
                                                            ))}
                                                            {temperLoading && <Option>Loading...</Option>}
                                                        </AutoOpenSelect>
                                                    </Form.Item>
                                                </Col>

                                                <Col sm={3}>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, "conversion"]}
                                                        label="Conversion"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: "Please enter valid conversion value",
                                                            },
                                                            {
                                                                validator: (_, value) => {
                                                                    if (!value) {
                                                                        return Promise.resolve();
                                                                    }
                                                                    const regex = /^[0-9]*\.?[0-9]+$/;
                                                                    if (regex.test(value) && parseFloat(value) > 0) {
                                                                        return Promise.resolve();
                                                                    }
                                                                    return Promise.reject(
                                                                        new Error("Conversion must be a positive number")
                                                                    );
                                                                },
                                                            },
                                                            createNumberValidationRule({
                                                                allowNegative: false,
                                                                maxBeforeDecimal: 10,
                                                                maxAfterDecimal: 3,
                                                                allowDecimal: true,
                                                                allowZero: true,
                                                            }),
                                                        ]}
                                                    >
                                                        <Input placeholder="0.0" />
                                                    </Form.Item>
                                                </Col>

                                                <Col sm={3}>
                                                    <Form.Item
                                                        {...restField}
                                                        name={[name, "remarks"]}
                                                        label="Remarks"
                                                    >
                                                        <Input.TextArea
                                                            placeholder="Enter Remarks"
                                                            maxLength={150}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                const formattedValue = value
                                                                    .toLowerCase()
                                                                    .replace(/\b\w/g, (char) => char.toUpperCase());
                                                                form.setFieldsValue({
                                                                    conversion_rate_items: {
                                                                        [name]: { remarks: formattedValue },
                                                                    },
                                                                });
                                                            }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </Col>

                                        {index !== 0 && (
                                            <Button
                                                type="primary"
                                                danger
                                                icon={<MinusCircleOutlined />}
                                                onClick={() => remove(name)}
                                                style={{
                                                    position: "absolute",
                                                    top: "8px",
                                                    right: "8px",
                                                    zIndex: 1,
                                                }}
                                                size="small"
                                            />
                                        )}
                                    </Row>
                                ))}

                                <Form.Item>
                                    <div className="w-100 flex justify-end">
                                        <Button
                                            type="primary"
                                            onClick={() => {
                                                if (fields.length < 50) {
                                                    dispatch(resetDieProfileData());
                                                    dispatch(fetchDieProfileDropdown({ page: 1, pagesize: 50 }));
                                                    setSearchDieProfile("");
                                                    add();
                                                } else {
                                                    message.error("You can only add up to 50 conversion rate items.");
                                                }
                                            }}
                                            icon={<PlusOutlined />}
                                        >
                                            Add Conversion Rate Item
                                        </Button>
                                    </div>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>

                    <Row justify="end" gutter={16} style={{ marginTop: "20px" }}>
                        <Col>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={btnLoading}
                                icon={<PlusOutlined />}
                            >
                                Update Conversion Rate
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </div>
        </>
    );
}

export default EditConversionRate;